<script setup lang="ts">
import MaterialCover from '../material/MaterialCover.vue';
import CoverFeatures from '../CoverFeatures.vue';
import { computed, ComputedRef, ref, watch } from 'vue';
import useLocale from '../helpers/use-locale';
import type { MaterialV1 } from '@ilteducation/content-registry-types';
import { useDisplay } from 'vuetify';
import { ContentWithDuplicates } from '@ilteducation/content';

export type MaterialChip = {
  icon: string;
  title: string;
  theme?: string;
  colorClass?: string;
};

export type ChipMap = Record<string, { icon: string; title: string }>;

const { t } = useLocale();
const { xs } = useDisplay();

type Emits = {
  (e: 'click-download'): void;
  (e: 'click-print'): void;
  (e: 'language-changed', material: MaterialV1): void;
};

const emit = defineEmits<Emits>();

type Props = {
  material: MaterialV1;
  previewUrl?: string;
  materialsWithSameOriginalTitle: MaterialV1[];
  isDownloadLoading: boolean;
  isPrintLoading: boolean;
  getLangI18nKey?: (code: string) => string;
  usePadding?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  previewUrl: '',
  getLangI18nKey: (code: string) => `languages.${code}`,
  usePadding: false,
});

const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

const currentMaterial = ref<MaterialV1>(props.material as MaterialV1);

watch(
  () => props.material,
  (newValue) => {
    if (newValue) {
      currentMaterial.value = newValue as MaterialV1;
    }
  },
  { immediate: true },
);
const mainLanguage = computed(() => ({
  language: capitalize(t(props.getLangI18nKey(currentMaterial.value.language))),
  code: currentMaterial.value.language,
}));
const defaultPropertyIcon = 'mdi-tag';

const otherLanguages = computed(() =>
  props.materialsWithSameOriginalTitle.map((duplicate: MaterialV1) => ({
    language: capitalize(t(props.getLangI18nKey(duplicate.language))),
    code: duplicate.language,
  })),
);

const audienceMap: ChipMap = {
  alone: {
    icon: 'mdi-account',
    title: 'label_audience_alone',
  },
  pair: {
    icon: 'mdi-account-supervisor',
    title: 'label_audience_pair',
  },
  group: {
    icon: 'mdi-account-group',
    title: 'label_audience_group',
  },
  class: {
    icon: 'mdi-town-hall',
    title: 'label_audience_class',
  },
};

const materialChips: ComputedRef<MaterialChip[]> = computed(() => {
  const result: MaterialChip[] = [];
  if (currentMaterial.value.properties?.audiences?.length > 0) {
    currentMaterial.value.properties.audiences.forEach((audience) => {
      if (audienceMap[audience]) {
        result.push(audienceMap[audience]);
      }
    });
  }
  return result;
});

const onLanguageChange = () => {
  const found = props.materialsWithSameOriginalTitle.find(
    (m: MaterialV1) => m.language === mainLanguage.value.code,
  ) as MaterialV1;
  if (found) {
    emit('language-changed', found);
    currentMaterial.value = found;
  }
};
</script>

<template>
  <v-container v-if="material" class="pa-0 material-container bg-surface-container-lowest">
    <div class="cover-image">
      <v-fade-transition mode="out-in">
        <div>
          <MaterialCover
            v-if="material"
            :id="currentMaterial.id"
            :preview-url="previewUrl"
            :alt="(currentMaterial as MaterialV1).title"
            :is-rounded="false"
            :height="300"
            aspect-ratio="16/9"
            class="bg-surface-container-highest"
          >
            <CoverFeatures
              class="material-cover-feat"
              :duplicates-count="(material as ContentWithDuplicates).duplicatesCount"
            />
          </MaterialCover>
        </div>
      </v-fade-transition>
    </div>
    <v-container :class="{ 'px-8': usePadding }">
      <div class="d-flex flex-wrap justify-space-between">
        <div class="mr-2 material-select">
          <v-select
            id="material-select"
            v-if="otherLanguages.length > 1"
            v-model="mainLanguage.code"
            class="w-100 my-2"
            :items="otherLanguages"
            color="primary"
            variant="outlined"
            rounded="md"
            density="comfortable"
            item-title="language"
            item-value="code"
            hide-details
            :label="t('label_language')"
            aria-describedby="material-select"
            prepend-inner-icon="mdi-web"
            :menu-props="{ style: 'z-index: 2500' }"
            @update:modelValue="onLanguageChange"
            role="button"
          ></v-select>
        </div>
        <div class="my-1">
          <!-- aria-label is needed since there is otherwise no accessible text on xs screens -->
          <v-btn
            color="primary"
            :icon="xs"
            class="mr-4 my-1 text-none elevation-0"
            :loading="isDownloadLoading"
            @click="emit('click-download')"
            :aria-label="t('button_download')"
          >
            <v-icon :class="{ 'pr-2': !xs }">mdi-download</v-icon>
            <span v-if="!xs">{{ t('button_download') }}</span>
          </v-btn>
          <v-btn
            color="primary"
            :icon="xs"
            class="my-1 text-none elevation-0"
            :loading="isPrintLoading"
            @click="emit('click-print')"
            :aria-label="t('button_print')"
          >
            <v-icon :class="{ 'pr-2': !xs }">mdi-printer</v-icon>
            <span v-if="!xs">{{ t('button_print') }}</span>
          </v-btn>
        </div>
      </div>
      <div class="mt-2">
        <slot name="title">
          <h2 class="title">{{ (currentMaterial as MaterialV1).title }}</h2>
        </slot>
      </div>
      <div class="mt-4">
        <p class="description" v-html="(currentMaterial as MaterialV1).description"></p>
      </div>
      <div class="mt-4">
        <template v-if="$slots.audience || materialChips.length > 0">
          <p class="mb-4 category-label">{{ t('label_audience') }}</p>
          <ul class="d-flex flex-wrap pa-0 ma-0 chip-list" :aria-label="t('label_audience')">
            <slot name="audience-ages"></slot>
            <v-chip
              v-for="chip in materialChips"
              :key="chip.title"
              class="mr-4 mb-4 bg-secondary-container"
              color="on-surface"
              label
              :prepend-icon="chip.icon"
              variant="flat"
              role="listitem"
              tag="li"
            >
              {{ t(`${chip.title}`) }}
            </v-chip>
          </ul>
        </template>
        <template v-if="$slots.bookType">
          <p class="mb-4 category-label">{{ t('label_book_format') }}</p>
          <ul class="pa-0 ma-0 chip-list" :aria-label="t('label_book_format')">
            <slot name="bookType"></slot>
          </ul>
        </template>
        <template v-if="material.keywords && material.keywords.length > 0">
          <p class="mb-4 category-label">{{ t('label_category') }}</p>
          <ul class="pa-0 ma-0 chip-list" :aria-label="t('label_category')">
            <v-chip
              v-for="keyword in material.keywords"
              :key="keyword"
              class="mr-4 mb-4 bg-tertiary-container"
              color="on-tertiary"
              label
              :prepend-icon="defaultPropertyIcon"
              variant="flat"
              role="listitem"
              tag="li"
            >
              {{ t(`material_keywords.${keyword}`) }}
            </v-chip>
          </ul>
        </template>
        <!-- Slot for additional content after the Categories section -->
        <slot name="after"></slot>
      </div>
      <!--    Related content. For next iterations-->

      <!--    <div class="mt-4">-->
      <!--      <H3>{{ t(l.label_related_content) }}</H3>-->
      <!--      <InfiniteContentGrid-->
      <!--        v-slot="{ content }"-->
      <!--        class="search-content-result"-->
      <!--        :is-loading="isSearchLoading"-->
      <!--        :contents="result as unknown as MaterialV1[]"-->
      <!--        @load-more="next"-->
      <!--      >-->
      <!--        <MaterialPreview :id="content.id" :title="content.title" :description="content.description" />-->
      <!--      </InfiniteContentGrid>-->
      <!--    </div>-->
    </v-container>
  </v-container>
</template>

<style scoped lang="scss">
.bg-material-cover {
  display: none;
}
.material-container {
  max-width: 730px;
  height: 100%;
}
.cover-image {
  min-height: 150px;
}
:deep(.v-overlay) {
  z-index: 20000 !important;
}
:deep(.v-field__prepend-inner > .v-icon) {
  opacity: 1 !important;
}
.category-label {
  font-size: 12px;
  font-weight: 500;
}
.material-select {
  width: 200px;
}
.title {
  font-size: 1.5rem;
}

.chip-list {
  list-style: none;
}

:deep(.description) {
  ul {
    margin-left: 1em;
  }
}
</style>
